/**
 * propTextures — biome-prop SVG generators for the Environmental Props system
 * (ENVIRONMENTAL_PROPS.md). Tier 1: pig_beach. Tier 1.5: tidewend.
 * Tier 2: brackenmoor. Tier 3: coil_undercroft. Lanternwood landmark.
 *
 * Decomposed into `./props/legacyNaturePropTextures.ts` and modular fixture
 * generators to satisfy the 500-LOC decomposition invariant.
 */
import { journalPageBody } from './journalPageTexture';
import { PROP_BASE_SIZE, PROP_DEFAULT_TINT, BIOME_ACCENTS, type PropKind } from './propTextureCatalog';
export { PROP_KINDS, isPropKind } from './propTextureCatalog';
export type { PropKind } from './propTextureCatalog';
import { buildGroundSignBody } from './props/townMountedFixtureTexture';
import { villageHouseBody } from './props/townHouseTexture';
import { lanternPostBody } from './props/townLanternTexture';
import { dockBody } from './props/townDockTexture';
import { buildTownTemplateBody } from './props/townTemplateTexture';
import { TOWN_PROP_TEMPLATES, resolveTownPropTemplate, townPropPalette, type TownPropPaletteOverrides } from './props/townPropTemplates';
import {
  palmBody,
  beachRockBody,
  tidePoolBody,
  trinketBody,
  pigPenBody,
  villageWellBody,
  autumnLeafBody,
  deadBrackenBody,
  moorStoneBody,
  mistShelfBody,
  fallenLogBody,
  gateBody,
  colonnadeColumnBody,
  brokenPillarBody,
  daisBody,
  spiralLedgeBody,
  bioluminescentFungusBody,
  gnarledTreeBody,
} from './props/legacyNaturePropTextures';

/**
 * Generate raw SVG markup for a prop kind. `size` is the target height in px
 * (defaults to the kind's intrinsic height — width scales by the kind's fixed
 * aspect); `tint` is the placement-picked palette color baked in as the
 * prop's primary (defaults to the spec primary).
 */
export function generatePropSVG(
  kind: PropKind,
  biome: string,
  size?: number,
  tint?: number,
  template?: string,
  palette?: TownPropPaletteOverrides,
): string {
  const accents = BIOME_ACCENTS[biome] ?? BIOME_ACCENTS.pig_beach;
  const primary = tint ?? PROP_DEFAULT_TINT[kind];
  const resolvedTemplate = resolveTownPropTemplate(kind, template);
  const base = resolvedTemplate ? TOWN_PROP_TEMPLATES[resolvedTemplate].frame : PROP_BASE_SIZE[kind];
  // Named architecture is baked at 2x for crisp window/cage details at the
  // existing town display sizes. Legacy texture dimensions remain unchanged.
  const scale = (size ?? base.h * (resolvedTemplate ? 2 : 1)) / base.h;
  const w = Math.max(1, Math.round(base.w * scale));
  const h = Math.max(1, Math.round(base.h * scale));

  let body: string;
  if (resolvedTemplate) body = buildTownTemplateBody(resolvedTemplate, townPropPalette(primary, palette), biome);
  else switch (kind) {
  case 'palm_tree': body = palmBody(primary); break;
  case 'beach_rock': body = beachRockBody(primary, accents.sand); break;
  case 'tide_pool': body = tidePoolBody(primary, accents.sand, accents.tidePoolStyle); break;
  case 'trinket': body = trinketBody(primary); break;
  case 'pig_pen': body = pigPenBody(primary); break;
  case 'village_house': body = villageHouseBody(primary); break;
  case 'village_well': body = villageWellBody(primary); break;
  case 'lantern_post': body = lanternPostBody(primary); break;
  case 'dock': body = dockBody(primary); break;
  case 'autumn_leaf': body = autumnLeafBody(primary); break;
  case 'dead_bracken': body = deadBrackenBody(primary); break;
  case 'moor_stone': body = moorStoneBody(primary); break;
  case 'mist_shelf': body = mistShelfBody(primary); break;
  case 'fallen_log': body = fallenLogBody(primary); break;
  case 'gate': body = gateBody(primary); break;
  case 'journal_page': body = journalPageBody(primary); break;
  case 'colonnade_column': body = colonnadeColumnBody(primary); break;
  case 'broken_pillar': body = brokenPillarBody(primary); break;
  case 'dais': body = daisBody(primary); break;
  case 'spiral_ledge': body = spiralLedgeBody(primary); break;
  case 'bioluminescent_fungus': body = bioluminescentFungusBody(primary); break;
  case 'gnarled_tree': body = gnarledTreeBody(primary); break;
  case 'sign': body = buildGroundSignBody(townPropPalette(primary)); break;
  }

  return `<svg width="${w}" height="${h}" viewBox="0 0 ${base.w} ${base.h}" xmlns="http://www.w3.org/2000/svg">${body}
  </svg>`;
}
